import React, { useContext, useState } from 'react';
import { Box, BoxProps, useTheme } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { FolderModel } from 'entities/folder/model/folderModel';
import DesktopFolderNameCell from 'shared/lib/extendedComponents/grids/components/folderNameCell/desktopFolderNameCell';
import { useNavigate } from 'react-router-dom';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { observer } from 'mobx-react-lite';
import IconTreeLine from 'shared/icons/iconTreeLines';
import { map } from 'lodash-es';
import FileOrTableItem from 'pages/searchPage/components/fileOrTableItem/fileOrTableItem';
import GridRowContainer from 'shared/lib/extendedComponents/grids/components/gridRowContainer';
import { FilesAndTablesHelper } from 'pages/searchPage/utils/filesAndTablesHelper';
import { SortingValue } from 'shared/lib/components/controls/sorting/sortingControl';
import { ExplorerEntityTypes } from 'entities/types';

export default observer(function FolderItem({
  folderModel,
  isOnlyFolder,
  sort,
  ...props
}: BoxProps & {
  folderModel: FolderModel;
  isOnlyFolder?: boolean;
  sort: SortingValue | null;
}) {
  const {
    spacings: { sp, search },
  } = useContext(UiContext);
  const navigate = useNavigate();
  const {
    getFolderSearchParams,
    getFileSearchParams,
    getSectionSearchParams,
    getTableSearchParams,
  } = useSearchParamsUtils();
  const theme = useTheme();

  const [firstItemY, setFirstItemY] = useState(0);
  const [svgY, setSvgY] = useState(0);
  const [itemHeight, setItemHeight] = useState(0);

  const filesAmount = folderModel.files?.length || 0;
  const tablesAmount = folderModel.tables?.length || 0;
  const totalAmount = isOnlyFolder ? 0 : filesAmount + tablesAmount;

  const navigateToSection = ({ sectionId }: { sectionId: string }) => {
    navigate({
      pathname: '/explorer',
      search: getSectionSearchParams({
        sectionId,
      }),
    });
  };

  const navigateToFolder = ({ folderId }: { folderId: string }) => {
    navigate({
      pathname: '/explorer',
      search: getFolderSearchParams({
        folderId,
      }),
    });
  };

  const navigateToFile = ({ fileId }: { fileId: string }) => {
    navigate({
      pathname: '/explorer',
      search: getFileSearchParams({
        fileId,
      }),
    });
  };

  const navigateToTable = ({ tableId }: { tableId: string }) => {
    navigate({
      pathname: '/explorer',
      search: getTableSearchParams({
        tableId,
      }),
    });
  };

  const filesHelper = new FilesAndTablesHelper({
    files: folderModel.files,
    tables: folderModel.tables,
    sort: sort || SortingValue.UPDATING_DEC,
  });

  let i = 0;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      <GridRowContainer
        hardOverrideSx={{
          position: 'relative',
          cursor: 'pointer',
          pl: `${search.firstLvlWidth + 20}px`,
        }}
        onClick={() => {
          if (folderModel.sectionId) {
            if (folderModel.isUnsortedFolder) {
              navigateToSection({ sectionId: folderModel.sectionId });
              return;
            }

            navigateToFolder({
              folderId: folderModel.id,
            });
          }
        }}
      >
        <DesktopFolderNameCell
          overrideColor={
            isOnlyFolder ? undefined : theme.palette.text.secondary
          }
          name={folderModel.name || ''}
        ></DesktopFolderNameCell>
        <Box
          sx={{ display: 'flex', position: 'absolute', top: '50%', left: 0 }}
        >
          <IconTreeLine
            ref={(el) => {
              if (el) {
                setSvgY(el.getBoundingClientRect().y);
              }
            }}
            count={totalAmount}
            regularHeight={itemHeight}
            firstHeight={firstItemY - svgY}
          ></IconTreeLine>
        </Box>
      </GridRowContainer>

      {!isOnlyFolder && (
        <Box
          ref={(el: null | HTMLDivElement) => {
            if (el) {
              const gapSize = parseFloat(theme.spacing(sp.s40)) * 2 + 1;
              const clearItemHeight =
                (el.offsetHeight - gapSize * (totalAmount - 1)) / totalAmount;
              setItemHeight(clearItemHeight + gapSize);
            }
          }}
          sx={{
            'display': 'flex',
            'flexDirection': 'column',
            'pl': `${search.secondLvlWidth + 20}px`,
            '& > *': {
              '&:not(:first-of-type)': {
                pt: sp.s40,
              },
              '&:not(:last-of-type)': {
                pb: sp.s40,
                borderBottom: 1,
                borderColor: 'custom.borders',
              },
            },
          }}
        >
          {map(filesHelper.content, (item) => {
            i += 1;
            return (
              <Box
                key={item.id}
                {...(i === 1
                  ? {
                      ref: (el: null | HTMLDivElement) => {
                        if (el) {
                          const rect = el.getBoundingClientRect();

                          const itemPb =
                            parseFloat(
                              getComputedStyle(el).getPropertyValue(
                                'padding-bottom'
                              )
                            ) || 0;

                          setFirstItemY(
                            (rect.top + (rect.bottom - itemPb)) / 2
                          );
                        }
                      },
                    }
                  : {})}
              >
                <GridRowContainer
                  onClick={() => {
                    if (folderModel.sectionId) {
                      if (item.type === ExplorerEntityTypes.FILE) {
                        if (folderModel.isUnsortedFolder) {
                          navigateToFile({
                            fileId: item.id,
                          });
                          return;
                        }

                        navigateToFile({
                          fileId: item.id,
                        });
                      } else {
                        if (folderModel.isUnsortedFolder) {
                          navigateToTable({
                            tableId: item.id,
                          });
                          return;
                        }

                        navigateToTable({
                          tableId: item.id,
                        });
                      }
                    }
                  }}
                  hardOverrideSx={{ cursor: 'pointer' }}
                >
                  <FileOrTableItem itemModel={item}></FileOrTableItem>
                </GridRowContainer>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
});
